import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Paper, Stack, Typography, Tooltip, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ShopifyOrderErrorViewModel } from "../../Types/Integration/OrderErrorViewModel ";
import { ListQuery, QueryFilter } from "../../Types/ExportTypes";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { Add as AddIcon, Queue as QueueIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";
import ResponsivePagination from "../ResponsivePagination";
interface ShopifyOrderErrorsProps {
    integrationId: number;
}

const ShopifyOrderErrors: React.FC<ShopifyOrderErrorsProps> = ({ integrationId }) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [errors, setErrors] = useState<ShopifyOrderErrorViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isFetching, setIsFetching] = useState(false);

    const loadErrors = useCallback(async () => {
        setIsFetching(true);
        try {
            const response = await authService.post<{ items: ShopifyOrderErrorViewModel[]; count: number }>(
                `/integrationApi/GetShopifyOrderErrors?integrationId=${integrationId}`,
                query
            );
            setErrors(response.items ?? []);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.failedToLoad", "Failed to load data")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
            setErrors([]);
        } finally {
            setIsFetching(false);
        }
    }, [authService, query, integrationId, showSnackbar, t]);

    useEffect(() => {
        if (!integrationId) return;
        loadErrors();
    }, [integrationId, loadErrors]);

    const formatDate = (value?: string | null) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString();
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };


    const handleRequeue = async (error: ShopifyOrderErrorViewModel) => {
        showLoading();
        try {
            await authService.post(`/integrationApi/RequeueShopifyOrder?integrationId=${integrationId}`, error);
            showSnackbar(t("dialog.actionCompleted", "Action completed"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.actionFailed", "Action failed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    const handleRemove = async (error: ShopifyOrderErrorViewModel) => {
        if (!window.confirm(t("dialog.areYouSure"))) return;
        showLoading();
        try {
            await authService.post(`/integrationApi/RemoveShopifyOrderError?integrationId=${integrationId}`, error);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.deleteFailed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    const handleRequeueAll = async () => {
        if (!errors.length) return;
        showLoading();
        try {
            await authService.post(`/integrationApi/RequeueAllShopifyOrders?integrationId=${integrationId}`, {});
            showSnackbar(t("dialog.actionCompleted", "Action completed"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.actionFailed", "Action failed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    return (
        <Card sx={{
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "none"
        }}>
            <CardHeader
                title={t("common.orderErrors")}
                action={
                    <Button variant="contained" size="small" onClick={handleRequeueAll} disabled={!errors.length}>
                        {t("common.requeueAll", "Requeue all")}
                    </Button>
                }
            />
            <CardContent>
                {isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : errors.length === 0 ? (
                    <Typography color="text.secondary">
                        {t("common.noEntriesFound")}
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {errors.map((error) => (
                            <Paper
                                key={String(error.id ?? `${error.orderNo ?? "order"}-${error.created ?? "created"}`)}
                                sx={{ p: 2 }}
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                                    <Box>
                                        <Typography variant="subtitle1">
                                            {t("common.orderNo")}: {error.orderNo}, id:{error.orderId}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                            {error.errorMessageLatest ?? t("common.noInformation", "No error message provided")}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t("common.retries", "Retries")}: {error.retries ?? 0} • {t("common.created", "Created")}: {formatDate(error.created)}
                                            {error.updated && ` • ${t("common.updated", "Updated")}: ${formatDate(error.updated)}`}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">

                                        <Tooltip title={t("common.requeue")}>
                                            <IconButton size="small" color="primary" onClick={() => handleRequeue(error)}>
                                                <QueueIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("common.delete")}>
                                            <IconButton size="small" color="error" onClick={() => handleRemove(error)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                              
                                        
                            
                                        
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}
                <ResponsivePagination
                    sx={{ mt: 2} }
                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
            </CardContent>
        </Card>
    );
};

export default ShopifyOrderErrors;
